import { defineConfiguratorElement } from "@/widget/defineElement";
import widgetStyles from "@/styles/widget.css?inline";
import { buildBundledFontFaceCss } from "@/widget/fonts";
import { DEFAULT_WIDGET_ASSETS_BASE_URL } from "@/shared/config/constants";

// Auto-define for drop-in usage in host pages.
// If you want manual control, import and call `defineConfiguratorElement()` yourself.
if (typeof window !== "undefined") {
  // Try to detect script URL from multiple sources
  let scriptUrl: string | undefined;

  // 1. Try document.currentScript (works for sync scripts)
  const currentScript = document.currentScript as HTMLScriptElement | null;
  if (currentScript?.src) {
    scriptUrl = currentScript.src;
  }

  // 2. Fallback: find script by filename pattern in all scripts
  if (!scriptUrl) {
    const scripts = Array.from(
      document.querySelectorAll<HTMLScriptElement>("script[src]"),
    );
    const widgetScript = scripts.find((s) =>
      s.src.includes("configurator-widget"),
    );
    if (widgetScript?.src) {
      scriptUrl = widgetScript.src;
    }
  }

  // 3. Determine base URL for relative paths in the default widget config.
  // Load assets from the same origin the widget script was served from. The
  // host page already trusts that origin (it loaded this script from there),
  // so its CSP allows the requests. Pointing assets at a different default
  // origin causes CSP violations on third-party pages. Hosts can still override
  // via the `data-assets-base-url` attribute.
  let defaultAssetsBaseUrl: string;
  if (scriptUrl) {
    defaultAssetsBaseUrl = new URL("/", scriptUrl).toString();
  } else {
    defaultAssetsBaseUrl = DEFAULT_WIDGET_ASSETS_BASE_URL;
  }

  // Self-hosted fonts load from the widget origin (script origin), independent
  // of any per-element data-assets-base-url override.
  const fontFaceCSS = buildBundledFontFaceCss(defaultAssetsBaseUrl);

  defineConfiguratorElement({
    defaultAssetsBaseUrl,
    fontFaceText: fontFaceCSS,
    stylesText: widgetStyles,
  });
}

export { defineConfiguratorElement };
